import React, { useState, useEffect } from "react";
import { cn } from "../lib/utils";

export interface ContainerTextFlipProps {
  /** Array of words to cycle through in the animation */
  words?: string[];
  /** Time in milliseconds between word transitions */
  interval?: number;
  /** Additional CSS classes to apply to the container */
  className?: string;
  /** Additional CSS classes to apply to the text */
  textClassName?: string;
  /** Typing speed in milliseconds per character */
  typingSpeed?: number;
  /** Delay before deleting word in milliseconds */
  deleteDelay?: number;
  /** Deleting speed in milliseconds per character */
  deleteSpeed?: number;
}

export function ContainerTextFlip({
  words = ["better", "modern", "beautiful", "awesome"],
  interval = 3000,
  className,
  textClassName,
  typingSpeed = 100,
  deleteDelay = 2000,
  deleteSpeed = 50,
}: ContainerTextFlipProps) {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);

  const currentWord = words[currentWordIndex];

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (!isDeleting) {
      // Typing effect
      if (charIndex < currentWord.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentWord.substring(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        }, typingSpeed);
      } else {
        // Word is complete, wait before deleting
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, deleteDelay);
      }
    } else {
      // Deleting effect
      if (charIndex > 0) {
        timeout = setTimeout(() => {
          setDisplayText(currentWord.substring(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        }, deleteSpeed);
      } else {
        // Word is deleted, move to next word
        setIsDeleting(false);
        setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
        setCharIndex(0);
        setDisplayText("");
      }
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [charIndex, isDeleting, currentWord, words, typingSpeed, deleteDelay, deleteSpeed]);

  return (
    <span
      className={cn(
        "inline-block text-left whitespace-nowrap",
        "text-white",
        "text-2xl sm:text-3xl md:text-4xl lg:text-7xl",
        "font-bold",
        className,
        textClassName,
      )}
    >
      {displayText}
      <span className="animate-pulse">|</span>
    </span>
  );
}
